import { Component } from '@theme/component';
import { StandardEvents, CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

const UNDO_NOTICE_DURATION = 6000;
const RESTORED_NOTICE_DURATION = 2400;

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number } | null} */
  #removedItem = null;
  /** @type {number | undefined} */
  #noticeTimeout;
  /** @type {HTMLElement | null} */
  #linkListenerTarget = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('cart:item-removed', this.#handleItemRemoved);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    // Listen on the drawer itself so links in the recommendations panel beside it are included,
    // and capture so this runs before the backdrop handler starts its own close.
    this.#linkListenerTarget = this.#themeDrawer;
    this.#linkListenerTarget?.addEventListener('click', this.#handleLinkClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('cart:item-removed', this.#handleItemRemoved);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#linkListenerTarget?.removeEventListener('click', this.#handleLinkClick, true);
    this.#linkListenerTarget = null;
    if (this.#noticeTimeout) window.clearTimeout(this.#noticeTimeout);
  }

  /**
   * MineraliA: a link inside the open drawer (a cart line or a recommendation)
   * slides the drawer closed first and then navigates, so the page change
   * never cuts the exit animation.
   * @param {MouseEvent} event
   */
  #handleLinkClick = async (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.hasAttribute('download')) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;

    const drawer = this.#themeDrawer;
    if (!drawer?.isOpen) return;

    event.preventDefault();
    await drawer.close();
    window.location.assign(url.href);
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#hydrateEmptyCartRecommendations();

    const section = document.getElementById('shopify-section-cart-drawer-section');
    if (section?.hasAttribute('data-cart-needs-refresh')) {
      sectionRenderer
        .renderSection('cart-drawer-section', { cache: false, mode: 'hydration' })
        .then(() => {
          section.removeAttribute('data-cart-needs-refresh');
          this.#updateStickyState();
          this.#hydrateEmptyCartRecommendations();
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[cart-drawer] Could not reconcile cart view:', error);
        });
    }

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#hydrateEmptyCartRecommendations();
          });

        if (event.action === 'add' && !detail?.didError) {
          const variantId = event.lines?.[0]?.merchandiseId ?? event.lines?.[0]?.id;
          this.#highlightAddedRow(variantId);
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /** @param {CustomEvent} event */
  #handleItemRemoved = (event) => {
    const item = event.detail?.item;
    if (!item?.id || !item?.quantity) return;

    this.#removedItem = item;
    this.#showNotice(this.dataset.removedText || 'Producto eliminado del carrito.', true);
  };

  /** Restores the last removed cart line. */
  async undoRemove() {
    const item = this.#removedItem;
    const notice = this.querySelector('[data-cart-drawer-notice]');
    const action = this.querySelector('[data-cart-drawer-undo]');
    if (!item || !(notice instanceof HTMLElement) || !(action instanceof HTMLButtonElement)) return;

    if (this.#noticeTimeout) window.clearTimeout(this.#noticeTimeout);
    notice.setAttribute('aria-busy', 'true');
    action.disabled = true;
    this.#setNoticeMessage(this.dataset.restoringText || 'Restaurando producto...');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : null))
      .filter(Boolean);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(item.id), quantity: item.quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [item],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        throw new Error(responseData.description || responseData.message || 'Could not restore cart item');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) throw new Error('Could not refresh cart');
      const cart = await cartResponse.json();

      this.#highlightAddedRow(item.id);
      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: responseData.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-drawer-undo',
          didError: false,
        },
      });

      this.#removedItem = null;
      notice.removeAttribute('aria-busy');
      action.hidden = true;
      this.#setNoticeMessage(this.dataset.restoredText || 'Producto restaurado.');
      this.#noticeTimeout = window.setTimeout(() => this.#hideNotice(), RESTORED_NOTICE_DURATION);
    } catch (error) {
      console.warn('[cart-drawer] Could not restore cart item:', error);
      deferredUpdatePromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Could not restore cart item',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      notice.removeAttribute('aria-busy');
      notice.setAttribute('aria-live', 'assertive');
      action.disabled = false;
      this.#setNoticeMessage(this.dataset.restoreErrorText || 'No pudimos restaurar el producto. Intenta de nuevo.');
    }
  }

  /** @param {string} message @param {boolean} showAction */
  #showNotice(message, showAction) {
    const notice = this.querySelector('[data-cart-drawer-notice]');
    const action = this.querySelector('[data-cart-drawer-undo]');
    if (!(notice instanceof HTMLElement) || !(action instanceof HTMLButtonElement)) return;

    if (this.#noticeTimeout) window.clearTimeout(this.#noticeTimeout);
    notice.hidden = false;
    notice.removeAttribute('aria-busy');
    notice.setAttribute('aria-live', 'polite');
    action.hidden = !showAction;
    action.disabled = false;
    this.#setNoticeMessage(message);
    this.#noticeTimeout = window.setTimeout(() => this.#hideNotice(), UNDO_NOTICE_DURATION);
  }

  /** @param {string} message */
  #setNoticeMessage(message) {
    const messageElement = this.querySelector('[data-cart-drawer-notice-message]');
    if (messageElement) messageElement.textContent = message;
  }

  #hideNotice() {
    const notice = this.querySelector('[data-cart-drawer-notice]');
    if (notice instanceof HTMLElement) notice.hidden = true;
    this.#removedItem = null;
  }

  /** @param {string | number | undefined} variantId */
  #highlightAddedRow(variantId) {
    if (!variantId) return;

    const selector = `#cart-drawer .cart-items__table-row[data-variant-id="${CSS.escape(String(variantId))}"]`;
    const applyHighlight = () => document.querySelector(selector)?.classList.add('cart-items__table-row--just-added');
    const observer = new MutationObserver(applyHighlight);

    applyHighlight();
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => {
      observer.disconnect();
      document.querySelectorAll(selector).forEach((row) => row.classList.remove('cart-items__table-row--just-added'));
    }, 1800);
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Replaces the empty-cart collection fallback with recently viewed products.
   * The fallback remains untouched when there is no local history or the
   * Shopify search request fails.
   */
  async #hydrateEmptyCartRecommendations() {
    const slot = this.querySelector('[data-cart-empty-recommendations-slot]');
    const template = this.querySelector('#empty-cart-recommendations-template');
    if (!(slot instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    if (!slot.querySelector('[data-cart-empty-recommendations]')) {
      slot.append(template.content.cloneNode(true));
    }

    const recommendations = slot.querySelector('[data-cart-empty-recommendations]');
    if (!(recommendations instanceof HTMLElement) || recommendations.dataset.state) return;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) {
      recommendations.dataset.state = 'fallback';
      return;
    }

    const list = recommendations.querySelector('[data-cart-empty-recommendations-list]');
    const title = recommendations.querySelector('[data-cart-empty-recommendations-title]');
    const status = recommendations.querySelector('[data-cart-empty-recommendations-status]');
    if (!(list instanceof HTMLUListElement)) return;

    recommendations.dataset.state = 'loading';
    recommendations.setAttribute('aria-busy', 'true');
    if (status) status.textContent = recommendations.dataset.loadingText || '';

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const markup = await sectionRenderer.getSectionHTML('predictive-search', false, url);
      const parsedMarkup = new DOMParser().parseFromString(markup, 'text/html');
      const recentCards = parsedMarkup.querySelectorAll(
        '#predictive-search-products .predictive-search-results__card--product'
      );
      if (recentCards.length === 0) throw new Error('Recently viewed products were not returned');

      const fragment = document.createDocumentFragment();
      for (const card of recentCards) {
        const item = document.createElement('li');
        item.className = 'cart-empty-recommendations__item';
        item.append(...Array.from(card.childNodes, (node) => node.cloneNode(true)));
        fragment.append(item);
      }

      list.replaceChildren(fragment);
      recommendations.hidden = false;
      recommendations.dataset.state = 'ready';
      if (title) title.textContent = recommendations.dataset.recentlyViewedTitle || 'Vistos recientemente';
      if (status) status.textContent = recommendations.dataset.recentlyViewedTitle || 'Vistos recientemente';
    } catch (error) {
      recommendations.dataset.state = 'fallback';
      if (title) title.textContent = recommendations.dataset.fallbackTitle || 'Recomendados para ti';
      if (status) status.textContent = recommendations.dataset.errorText || '';
      console.warn('[cart-drawer] Could not load recently viewed products:', error);
    } finally {
      recommendations.removeAttribute('aria-busy');
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;
    dialog.setAttribute('cart-summary-sticky', 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
